/**
 * One staff socket to nt-support's live chat, through this console's own
 * Worker (`worker/support-chat-socket.ts`): the inbox hub without a ticket
 * id, one chat's live rows with one.
 *
 * Push only: every write is an HTTP POST. The socket reconnects with full
 * jitter (spec 8.3), at once when the tab becomes visible or the network
 * returns, pings every 25 s while visible and reconnects when no pong comes
 * back within 10 s. The hub socket also sends a heartbeat every 60 s while
 * the tab is visible: that is what keeps the admin "available for chat".
 * A socket that never opens (live chat off on this environment) backs off to
 * one try every five minutes instead of retrying forever.
 */
import { useEffect, useRef, useState } from "react";

import {
  parseStaffFrame,
  reconnectDelayMs,
  socketUrl,
  type StaffFrame,
} from "@/lib/support-chat";

const PING_MS = 25_000;
const PONG_TIMEOUT_MS = 10_000;
const HEARTBEAT_MS = 60_000;
const STABLE_MS = 10_000;
const FAILED_OPENS_BEFORE_SLOW = 3;
const SLOW_RETRY_MS = 5 * 60_000;
/** A reconnect banner only after this long without a socket (spec 7.3). */
const BANNER_AFTER_MS = 3_000;

export interface StaffSocketOptions {
  /** Without it, the inbox hub. */
  ticketId?: string;
  enabled: boolean;
  /** The hub only: the availability heartbeat. */
  heartbeat?: boolean;
  onFrame: (frame: StaffFrame) => void;
}

export interface StaffSocket {
  connected: boolean;
  /** No socket for more than 3 s: time to say so. */
  reconnecting: boolean;
  /** Sends a client frame when the socket is open; dropped otherwise. */
  send: (frame: object) => void;
}

export function useStaffSocket(options: StaffSocketOptions): StaffSocket {
  const { ticketId, enabled, heartbeat = false } = options;
  const onFrame = useRef(options.onFrame);
  onFrame.current = options.onFrame;
  const socket = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    if (!enabled || typeof WebSocket === "undefined") return;
    let stopped = false;
    let attempt = 0;
    let failedOpens = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let stable: ReturnType<typeof setTimeout> | undefined;
    let pongDeadline: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;
    let beat: ReturnType<typeof setInterval> | undefined;
    let banner: ReturnType<typeof setTimeout> | undefined;
    const armBanner = () => {
      clearTimeout(banner);
      banner = setTimeout(() => setReconnecting(true), BANNER_AFTER_MS);
    };

    const visible = () => document.visibilityState === "visible";
    const sendRaw = (data: string) => {
      const ws = socket.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(data);
    };
    const sendHeartbeat = () => {
      if (heartbeat && visible())
        sendRaw(JSON.stringify({ v: 1, type: "heartbeat" }));
    };
    const clearTimers = () => {
      clearTimeout(stable);
      clearTimeout(pongDeadline);
      clearInterval(ping);
      clearInterval(beat);
    };

    const schedule = () => {
      if (stopped) return;
      clearTimeout(retry);
      const delay =
        failedOpens >= FAILED_OPENS_BEFORE_SLOW
          ? SLOW_RETRY_MS
          : reconnectDelayMs(attempt);
      attempt += 1;
      retry = setTimeout(connect, delay);
    };

    function connect() {
      if (stopped) return;
      clearTimeout(retry);
      let opened = false;
      const ws = new WebSocket(socketUrl(window.location, ticketId));
      socket.current = ws;
      ws.addEventListener("open", () => {
        opened = true;
        failedOpens = 0;
        clearTimeout(banner);
        setConnected(true);
        setReconnecting(false);
        stable = setTimeout(() => {
          attempt = 0;
        }, STABLE_MS);
        sendHeartbeat();
        ping = setInterval(() => {
          if (!visible()) return;
          sendRaw("ping");
          clearTimeout(pongDeadline);
          pongDeadline = setTimeout(
            () => ws.close(4000, "no pong"),
            PONG_TIMEOUT_MS,
          );
        }, PING_MS);
        if (heartbeat) beat = setInterval(sendHeartbeat, HEARTBEAT_MS);
      });
      ws.addEventListener("message", (event) => {
        // `onFrame` is the current render's handler, maybe for another chat.
        if (stopped) return;
        if (event.data === "pong") {
          clearTimeout(pongDeadline);
          return;
        }
        const frame = parseStaffFrame(event.data);
        if (frame) onFrame.current(frame);
      });
      ws.addEventListener("close", () => {
        clearTimers();
        if (socket.current === ws) socket.current = null;
        // A socket this effect closed on cleanup closes asynchronously: by
        // then a newer socket may be open, and this one must not flip it to
        // disconnected or arm a banner nobody clears.
        if (stopped) return;
        setConnected(false);
        if (opened) armBanner();
        if (!opened) failedOpens += 1;
        schedule();
      });
    }

    const wake = () => {
      if (stopped) return;
      if (socket.current === null) {
        failedOpens = Math.min(failedOpens, FAILED_OPENS_BEFORE_SLOW - 1);
        connect();
      } else sendHeartbeat();
    };
    const onVisibility = () => {
      if (visible()) wake();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", wake);
    armBanner();
    connect();

    return () => {
      stopped = true;
      clearTimeout(retry);
      clearTimeout(banner);
      clearTimers();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", wake);
      const ws = socket.current;
      socket.current = null;
      ws?.close(1000, "done");
      setConnected(false);
      setReconnecting(false);
    };
  }, [enabled, ticketId, heartbeat]);

  return {
    connected,
    reconnecting,
    send: (frame) => {
      const ws = socket.current;
      if (ws && ws.readyState === WebSocket.OPEN)
        ws.send(JSON.stringify(frame));
    },
  };
}
